import React, { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowUpRight, Play } from "lucide-react";
import { HERO_SLIDES } from "../lib/brand";

const AUTOPLAY_MS = 6500;

export const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(
    () => setIndex((i) => (i + 1) % HERO_SLIDES.length),
    []
  );
  const prev = () =>
    setIndex((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [next, paused, index]);

  const slide = HERO_SLIDES[index];

  return (
    <section
      id="home"
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-[#0F172A]"
    >
      {/* Slides */}
      <AnimatePresence mode="sync">
        <motion.div
          key={slide.image}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="h-full w-full object-cover"
            loading={index === 0 ? "eager" : "lazy"}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/85 via-[#0F172A]/55 to-[#0F172A]/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 mx-auto flex h-full max-w-7xl items-center px-6 lg:px-8">
        <div className="max-w-3xl pt-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 backdrop-blur">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00A3E0] opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00A3E0]" />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/85">
                  {slide.accent}
                </span>
              </div>
              <h1
                data-testid="hero-title"
                className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl"
              >
                {slide.title}
              </h1>
              <p className="mt-4 max-w-xl text-lg font-medium text-[#00A3E0]">
                {slide.subtitle}
              </p>
              <p className="mt-3 max-w-2xl text-base text-white/75">
                {slide.description}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/submit-project"
              data-testid="hero-submit-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#00A3E0] px-6 py-3 text-sm font-semibold text-white shadow-2xl shadow-[#00A3E0]/40 transition-all hover:translate-y-[-1px] hover:bg-[#0089bd]"
            >
              Submit Your Project
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </Link>
            <a
              href="#services"
              data-testid="hero-services-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition-all hover:bg-white/10"
            >
              <Play className="h-3.5 w-3.5 fill-current" />
              Explore Services
            </a>
          </div>

          {/* Slide indicators */}
          <div className="mt-12 flex items-center gap-3">
            {HERO_SLIDES.map((s, i) => (
              <button
                key={s.title}
                onClick={() => setIndex(i)}
                data-testid={`hero-indicator-${i}`}
                aria-label={`Go to slide ${i + 1}`}
                className={`group relative h-1 overflow-hidden rounded-full transition-all ${
                  i === index ? "w-16 bg-white/30" : "w-8 bg-white/20"
                }`}
              >
                {i === index && (
                  <motion.span
                    key={index}
                    initial={{ width: 0 }}
                    animate={{ width: paused ? "100%" : "100%" }}
                    transition={{ duration: paused ? 0.4 : AUTOPLAY_MS / 1000, ease: "linear" }}
                    className="absolute left-0 top-0 h-full bg-[#00A3E0]"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Side nav arrows */}
      <div className="absolute bottom-20 right-6 z-20 hidden gap-2 lg:flex">
        <button
          onClick={prev}
          data-testid="hero-prev"
          aria-label="Previous slide"
          className="rounded-full border border-white/25 bg-white/5 p-3 text-white backdrop-blur transition-all hover:bg-white/15"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={next}
          data-testid="hero-next"
          aria-label="Next slide"
          className="rounded-full border border-white/25 bg-white/5 p-3 text-white backdrop-blur transition-all hover:bg-white/15"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Sector marquee */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-[#0F172A]/60 backdrop-blur">
        <div className="overflow-hidden">
          <div className="marquee-track flex w-max items-center gap-12 py-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-white/55">
            {[...Array(2)].flatMap((_, k) =>
              [
                "Energy",
                "Real Estate",
                "Infrastructure",
                "Construction Materials",
                "Commodities",
                "Media & Communications",
              ].map((label, i) => (
                <span key={`${k}-${i}`} className="flex items-center gap-12">
                  <span>{label}</span>
                  <span className="text-[#00A3E0]">◆</span>
                </span>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
